// Pantalla principal de lista de chats con los que el usuario ha chateado (versión web).
// Usa hook useChatRooms para obtener datos y mantiene compatibilidad con el sistema original.
// Incluye realtime para actualizar chats nuevos.
// Adaptado para web desde amarsi-app.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatRooms } from '../../hooks/chat/useChatRooms';
import ChatRoomItem from './ChatRoomItem';
import { FiSearch, FiEdit, FiX, FiRefreshCw, FiRadio, FiInfo } from 'react-icons/fi';
import MyUserAvatar from '../radar/components/MyUserAvatar';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { supabase } from '../../lib/supabase';
import { chatService } from '../../services/chatService';

// Constante para tipos de filtro de mensajes
const msg_type = ['Todos', 'Sin Leer', 'Leidos'];

export default function ChatScreen() {
  const { data: chatrooms, isLoading, refetch, error } = useChatRooms();
  const navigate = useNavigate();

  // Obtiene la sesión del usuario para funciones de debug
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;

  // Estados locales para mantener compatibilidad con código legacy
  const [refreshing, setRefreshing] = useState(false);
  const [activeDeleteChatroom, setActiveDeleteChatroom] = useState(false);
  const [searchPerson, setSearchPerson] = useState('');
  const [filteredChatrooms, setFilteredChatrooms] = useState([]);

  const [msg_type_index, set_msg_type_index] = useState(0);
  
  // Referencia para debounce de búsqueda
  const searchTimeout = useRef(null);
  
  // Referencia para evitar múltiples refetches
  const lastRefetchTime = useRef(0);
  
  // Referencia para almacenar el canal de debug
  const debugChannelRef = useRef(null);

  // Función para refrescar chats forzando actualización desde servidor
  // Siempre invalida cache para obtener datos frescos (botón de debug)
  const onRefresh = useCallback(() => {
    console.log(' --- onRefresh: Iniciando refresco FORZADO de chats');
    setRefreshing(true);
    
    // Invalidar cache del servicio para forzar llamada al servidor
    chatService.invalidateAllChatCache();
    console.log(' --- onRefresh: Cache invalidado, forzando refresco desde servidor');
    
    // Forzar refresco ignorando cache de TanStack
    refetch().finally(() => {
      setRefreshing(false);
      console.log(' --- onRefresh: Refresco forzado completado');
    });
  }, [refetch]);

  // Función de debug para mostrar información actual de chatrooms en consola
  // const showDebugInfo = useCallback(() => {
  //   console.log('=== DEBUG CHATSCREEN ===');
  //   console.log('Usuario actual:', session?.user?.id);
  //   console.log('Total chatrooms (raw):', chatrooms?.length || 0);
  //   console.log('Filtered chatrooms:', filteredChatrooms?.length || 0);
  //   console.log('Estado de filtros:', {
  //     searchPerson,
  //     msg_type_index,
  //     msg_type_selected: msg_type[msg_type_index]
  //   });
  //   console.log('Datos de chatrooms (raw):', JSON.stringify(chatrooms, null, 2));
  //   console.log('Datos filtrados:', JSON.stringify(filteredChatrooms, null, 2));
  //   console.log('Estado de loading:', isLoading);
  //   console.log('Estado de error:', error?.message || 'Ninguno');
  //   console.log('========================');
  // }, [chatrooms, filteredChatrooms, session, searchPerson, msg_type_index, isLoading, error]);

  // Función para probar conexión realtime directamente desde ChatScreen
  // const testRealtimeConnection = useCallback(() => {
  //   if (!session?.user?.id) {
  //     console.log('=== DEBUG REALTIME: No hay sesión ===');
  //     return;
  //   }

  //   const userId = session.user.id;
  //   console.log('=== DEBUG REALTIME: Iniciando test para usuario:', userId);

  //   // Limpiar canal anterior si existe
  //   if (debugChannelRef.current) {
  //     console.log('=== DEBUG REALTIME: Limpiando canal anterior');
  //     supabase.removeChannel(debugChannelRef.current);
  //   }

  //   // Crear canal de prueba
  //   const testChannel = supabase.channel(`test_chats_${userId}`);
  //   debugChannelRef.current = testChannel;

  //   // Escuchar como remitente
  //   testChannel.on('postgres_changes', {
  //     event: '*',
  //     schema: 'public',
  //     table: 'mensajitos',
  //     filter: `remitente=eq.${userId}`,
  //   }, (payload) => {
  //     console.log('=== DEBUG REALTIME: Evento REMITENTE recibido:', payload);
  //   });

  //   // Escuchar como destinatario
  //   testChannel.on('postgres_changes', {
  //     event: '*',
  //     schema: 'public',
  //     table: 'mensajitos',
  //     filter: `destinatario=eq.${userId}`,
  //   }, (payload) => {
  //     console.log('=== DEBUG REALTIME: Evento DESTINATARIO recibido:', payload);
  //   });

  //   // Suscribir
  //   testChannel.subscribe((status) => {
  //     console.log('=== DEBUG REALTIME: Status del canal:', status);
  //     if (status === 'SUBSCRIBED') {
  //       console.log('=== DEBUG REALTIME: ✅ Canal de prueba suscrito exitosamente');
  //     } else {
  //       console.log('=== DEBUG REALTIME: ❌ Error en canal de prueba:', status);
  //     }
  //   });
  // }, [session?.user?.id]);

  // Función auxiliar para aplicar filtro de mensajes no leídos
  const applyMessageFilter = useCallback((messageTypeIndex) => {
    let filtered = chatrooms || [];

    if (messageTypeIndex === 1) { // 'Sin Leer'
      filtered = filtered.filter(chatroom => chatroom.sin_leer > 0);
    }
    
    // Si hay búsqueda por nombre, aplicarla también
    if (searchPerson.trim()) {
      const searchTerm = searchPerson.toLowerCase().trim();
      filtered = filtered.filter(chatroom => 
        chatroom.full_name && chatroom.full_name.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredChatrooms(filtered);
  }, [chatrooms, searchPerson]);

  // Efecto para manejar errores
  useEffect(() => {
    console.log('useEffect error:', error);
    if (error) {
      console.log(' --- getMisChats(error) ', error);
      console.log(' ');
      alert(error.message);
    }
  }, [error]);

  // Efecto para carga inicial optimizado
  useEffect(() => {
    if (!chatrooms && !isLoading) {
      console.log(' --- useEffect inicial: Cargando chats (primer carga)');
      refetch();
    }
  }, [refetch, chatrooms, isLoading]);

  // Efecto para sincronizar filteredChatrooms con chatrooms
  useEffect(() => {
    console.log(' --- useEffect sincronización: chatrooms cambiaron:', chatrooms?.length || 0);
    applyMessageFilter(msg_type_index);
  }, [chatrooms, msg_type_index, applyMessageFilter]);

  // Efecto para focus handler optimizado con debounce
  // Refresca chats cuando la pantalla gana foco, pero no muy seguido
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastRefetchTime.current < 2000) {
          console.log(' --- Focus handler: Ignorando refresco - muy reciente');
          return;
        }
        
        console.log(' --- Focus handler: Refrescando chats');
        lastRefetchTime.current = now;
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  // Función para filtrar chats por nombre del amigo optimizada con debounce
  const searchChatroomByFriendName = useCallback((name) => {
    console.log('[ChatScreen] Buscando chat por nombre:', name);
    setSearchPerson(name);
    
    // Limpiar timeout existente
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Configurar nuevo timeout para debounce
    searchTimeout.current = setTimeout(() => {
      if (!chatrooms || !name.trim()) {
        // Si no hay búsqueda, mostrar todos según el filtro actual
        applyMessageFilter(msg_type_index);
        return;
      }

      const searchTerm = name.toLowerCase().trim();
      const filteredByName = chatrooms.filter(chatroom => 
        chatroom.full_name && chatroom.full_name.toLowerCase().includes(searchTerm)
      );

      // Aplicar filtro de mensajes no leídos después del filtro por nombre
      if (msg_type_index === 1) { // 'Sin Leer'
        const finalFiltered = filteredByName.filter(chatroom => chatroom.sin_leer > 0);
        setFilteredChatrooms(finalFiltered);
      } else {
        setFilteredChatrooms(filteredByName);
      }
    }, 300); // 300ms debounce
  }, [chatrooms, msg_type_index, applyMessageFilter]);

  // Función para manejar cambios en el formulario de filtros
  const handleFormChange = useCallback(() => {
    console.log('[ChatScreen] Cambio en filtro de mensajes, índice:', msg_type_index);
    applyMessageFilter(msg_type_index);
  }, [msg_type_index, applyMessageFilter]);

  // Navega a la pantalla de perfil de amigo usando location.state para no exponer UUID
  const handleOpenFriendProfile = useCallback((friendId) => {
    navigate('/friend/profile', { state: { friendId } });
  }, [navigate]);

  // Cleanup para evitar memory leaks
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      // Limpiar canal de debug si existe
      if (debugChannelRef.current) {
        supabase.removeChannel(debugChannelRef.current);
      }
    };
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10">
        <div className="h-20 p-4 flex items-center justify-between border-b border-neutral-800 bg-neutral-900">
          <div className="flex gap-4 items-center">
            <MyUserAvatar bradius={50} showName={false} customSize={44} />
            <h2 className="text-2xl text-white">Chats</h2>
          </div>
          <div className="flex gap-2">
            {/* Botón de debug info */}
            {/* <button 
              className="p-2"
              onClick={showDebugInfo}
              title="Mostrar información de debug"
            >
              <FiInfo size={20} color="#DA0037" />
            </button> */}
            {/* Botón de test realtime */}
            {/* <button 
              className="p-2"
              onClick={testRealtimeConnection}
              title="Probar conexión realtime"
            >
              <FiRadio size={20} color="#00FF00" />
            </button> */}
            {/* Botón de refresh */}
            <button 
              className={`p-2 ${refreshing ? 'animate-spin' : ''}`}
              onClick={onRefresh}
              disabled={refreshing}
            >
              <FiRefreshCw size={24} color="white" />
            </button>
            {activeDeleteChatroom 
              ? (
                <button 
                  className="bg-primary/30 rounded-lg p-2"
                  onClick={() => setActiveDeleteChatroom(!activeDeleteChatroom)}
                >
                  <FiX size={24} color="white" />
                </button>
              ) : (
                <button 
                  className="p-2"
                  onClick={() => setActiveDeleteChatroom(!activeDeleteChatroom)}
                >
                  <FiEdit size={24} color="white" />
                </button>
              )
            }
          </div>
        </div>
        
        {/* Barra de búsqueda y filtros */}
        <div className="h-16 p-4 flex gap-2 items-center">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={20} />
            <input
              type="text"
              className="w-full bg-neutral-800 p-3 pl-10 text-base text-white rounded-lg outline-none focus:ring-2 focus:ring-primary"
              placeholder="Buscar por nombre"
              value={searchPerson}
              maxLength={20}
              onChange={(e) => searchChatroomByFriendName(e.target.value)}
            />
          </div>
          <select
            value={msg_type[msg_type_index]}
            onChange={(e) => {
              const index = msg_type.indexOf(e.target.value);
              set_msg_type_index(index);
              handleFormChange();
            }}
            className="bg-neutral-800 text-white text-base rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary"
            style={{ width: 120 }}
          >
            {msg_type.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de chats */}
      <div className=''>
        {isLoading ? (
          <div className="p-4 text-center">
            <p className="text-white">Cargando...</p>
          </div>
        ) : (
          <div className="h-[calc(100vh-4rem)] overflow-y-auto">
            {filteredChatrooms?.length > 0 ? (
              <div>
                {filteredChatrooms.map((doc, d) => (
                  <ChatRoomItem 
                    key={d} 
                    data={doc} 
                    showDeleteOption={activeDeleteChatroom}
                    onOpenFriendProfile={handleOpenFriendProfile}
                  />
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-white">
                  {searchPerson || msg_type_index === 1 
                    ? 'No se encontraron chats con los filtros aplicados.' 
                    : 'Ups, no hay mensajes aun.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
